import React from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { DELIVERY_SERIES_COLOR, fmtStamp, timeAxis, toChartDate } from '../lib/chartTheme';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

/**
 * The endurance an FCR-D market requires: 20 minutes — the reservoir must sustain the full
 * bid for a third of an hour in the market's direction.
 */
export const REQUIRED_ENDURANCE_H = 1 / 3;

export const ENDURANCE_SERIES_NAME = 'Endurance';

/**
 * The one series of the endurance chart: this market's mean endurance per settled period, in
 * hours. Exported for the tests — jsdom has no canvas.
 *
 * @param {import('../lib/deliveryChartRows').DeliveryChartRow[]} rows
 */
export const buildEnduranceSeries = rows => [
    {
        type: 'line',
        name: ENDURANCE_SERIES_NAME,
        color: DELIVERY_SERIES_COLOR.endurance,
        lineThickness: 1.8,
        markerSize: 3,
        markerType: 'circle',
        showInLegend: true,
        xValueType: 'dateTime',
        toolTipContent: '{name}: {y} h',
        dataPoints: rows.map(row => ({ x: toChartDate(row.t), y: row.enduranceH, t: row.t })),
    },
];

/**
 * The dotted line marking the required endurance — 20 min, i.e. 1/3 h — that the market's bid
 * must be backed for. Exported for the tests.
 */
export const requiredEnduranceStripLine = () => ({
    value: REQUIRED_ENDURANCE_H,
    color: DELIVERY_SERIES_COLOR.missed,
    thickness: 1.2,
    lineDashType: 'dot',
    label: 'Required 20 min',
    labelFontColor: DELIVERY_SERIES_COLOR.missed,
    labelFontSize: 11,
    labelBackgroundColor: 'transparent',
});

/**
 * How long the reservoir behind this market's bid would have sustained it, per settled
 * period, in hours — `endurance_mean_h` of the market's settled record — against the dotted
 * required-endurance line at 20 minutes. Null periods (not measured) break the line rather
 * than reading as zero hours.
 */
const EnduranceChart = ({ rows, from, to }) => {
    const options = {
        height: 180,
        zoomEnabled: true,
        animationEnabled: false,
        axisX: {
            ...timeAxis(Date.parse(from), Date.parse(to)),
            lineThickness: 1,
        },
        axisY: {
            title: 'h',
            minimum: 0,
            lineThickness: 1,
            gridThickness: 1,
            gridColor: '#eef0f2',
            stripLines: [requiredEnduranceStripLine()],
        },
        legend: { fontSize: 12 },
        toolTip: {
            contentFormatter: e => {
                const entry = e.entries[0];
                if (!entry || entry.dataPoint.y === null || entry.dataPoint.y === undefined) {
                    return null;
                }
                const stamp = entry.dataPoint.t ? fmtStamp(entry.dataPoint.t) : '';
                return `<div style="font-size:12px"><div style="color:#7e8389">${stamp}</div><span style="color:${entry.dataSeries.color}">${
                    entry.dataSeries.name
                }: ${entry.dataPoint.y.toFixed(2)} h</span></div>`;
            },
        },
        data: buildEnduranceSeries(rows),
    };

    return <CanvasJSChart options={options} />;
};

export default React.memo(EnduranceChart);
